/**
 * Shown for a submission that failed without explaining itself. Every rejection
 * that has not opted in through `UserFacingSubmissionError` is reduced to this,
 * so a raw failure is never rendered.
 */
export const SUBMIT_ERROR_MESSAGE = 'Your message could not be sent. Please try again.'

/**
 * A submission that failed away from the composer that would normally report it,
 * carried back so the composer can surface it as if the user had pressed Send
 * there.
 *
 * A turn composed on another page is dispatched by the host, outside the
 * composer's own submit path, so nothing else shows the failure or gives the
 * user their words back.
 */
export interface SubmissionFailure {
  /** Already reduced from the raw rejection, so it is safe to render. */
  message: string
  /** The composed text, restored into the composer so the turn is not lost. */
  text: string
}

/**
 * A submission failure whose message is written for the user and is safe to
 * render verbatim.
 *
 * Submission errors are otherwise reduced to one generic string, because an
 * arbitrary rejection (a network stack trace, a server payload) must never be
 * shown. Extending this class is how a failure opts in to explaining itself —
 * for example a handed-off turn that is too large to store, where the recovery
 * (remove an attachment) is not guessable from a generic message.
 */
export class UserFacingSubmissionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserFacingSubmissionError'
  }
}

/** Returns the message to show for a failed submission, including one wrapped by session handling. */
export function userFacingSubmissionMessage(error: unknown): string | undefined {
  const seen = new Set<Error>()
  let current = error

  while (current instanceof Error && !seen.has(current)) {
    if (current instanceof UserFacingSubmissionError && current.message) return current.message
    seen.add(current)
    current = current.cause
  }

  return undefined
}

/**
 * A submission that was abandoned rather than failed, so there is nothing to
 * report. Raised when the user leaves a surface whose submission never settles —
 * a handed-off turn that navigated away — and then returns to it, where the
 * composer must unlock without claiming the turn was lost.
 */
export class SubmissionAbandonedError extends Error {
  constructor(message = 'Submission abandoned.') {
    super(message)
    this.name = 'SubmissionAbandonedError'
  }
}

/** Whether a rejection represents an abandoned submission rather than a failure. */
export function isSubmissionAbandoned(error: unknown): boolean {
  return error instanceof SubmissionAbandonedError
}
